import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../config/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const DEFAULT_CENTER = { lat: 25.7617, lng: -80.1918 };
let googleMapsPromise = null;

function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in a browser.'));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured.'));
  }

  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load.')));
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Google Maps failed to load.'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

function buildSchoolAddress(school) {
  return [
    school?.street_address,
    school?.city,
    school?.state,
    school?.zip_code,
  ].filter(Boolean).join(', ');
}

async function geocodeSchoolCenter(apiKey, address) {
  if (!address) {
    return DEFAULT_CENTER;
  }

  const google = await loadGoogleMaps(apiKey);
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        resolve({
          lat: Number(results[0].geometry.location.lat().toFixed(7)),
          lng: Number(results[0].geometry.location.lng().toFixed(7)),
        });
        return;
      }

      resolve(DEFAULT_CENTER);
    });
  });
}

function formatCoordinate(value) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(6) : '--';
}

function formatTime(value) {
  if (!value) return 'Unknown';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';

  return parsed.toLocaleString();
}

function getMarkerLabel(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'P';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function getMarkerIcon(isHighlighted) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: isHighlighted ? 16 : 13,
    fillColor: isHighlighted ? '#ef4444' : '#2563eb',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 3,
  };
}

export default function ParentLocationDiagnosticsPage({ token, schoolId }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef(new Map());
  const hasAutoFramedRef = useRef(false);
  const shouldAutoFrameRef = useRef(true);
  const [school, setSchool] = useState(null);
  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [mapError, setMapError] = useState('');
  const [hoveredParentId, setHoveredParentId] = useState(null);
  const schoolAddress = useMemo(() => buildSchoolAddress(school), [school]);

  const loadLocations = useCallback(async ({ silent = false, autoFrame = !hasAutoFramedRef.current } = {}) => {
    if (!silent) setLoading(true);
    if (autoFrame) shouldAutoFrameRef.current = true;

    try {
      const [schoolRes, locationsRes, eventsRes] = await Promise.all([
        apiFetch(`/schools/${schoolId}`, {}, token),
        apiFetch(`/parent-location/school/${schoolId}/active?minutes=30`, {}, token),
        apiFetch(`/geofenced-data/school/${schoolId}?limit=200&orderBy=timestamp&orderDirection=DESC`, {}, token),
      ]);

      setSchool(schoolRes.data || null);
      setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      setLoadError('');
    } catch (error) {
      console.error('Failed to load parent location diagnostics:', error);
      setLoadError(error.message || 'Unable to load diagnostics data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const intervalId = window.setInterval(() => {
      loadLocations({ silent: true });
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [loadLocations]);

  useEffect(() => {
    let cancelled = false;
    const markerMap = markersRef.current;

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(async (google) => {
      if (cancelled || !mapEl.current) return;

        if (!mapRef.current) {
          const center = await geocodeSchoolCenter(GOOGLE_MAPS_API_KEY, schoolAddress);
          if (cancelled || !mapEl.current) return;

          mapRef.current = new google.maps.Map(mapEl.current, {
            center,
            zoom: 15,
            mapTypeId: 'roadmap',
            clickableIcons: false,
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: true,
            gestureHandling: 'greedy',
          });
        }
        infoWindowRef.current = new google.maps.InfoWindow();
        setMapError('');
      })
      .catch((error) => {
        if (!cancelled) {
          setMapError(error.message || 'Unable to load the diagnostics map.');
        }
      });

    return () => {
      cancelled = true;
      markerMap.forEach((marker) => marker.setMap(null));
      markerMap.clear();
    };
  }, [schoolAddress]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    const nextMarkerIds = new Set();

    let highlightedExists = false;

    locations.forEach((location) => {
      const parentId = Number(location.contact_id);
      const position = {
        lat: Number(location.latitude),
        lng: Number(location.longitude),
      };

      if (!Number.isFinite(parentId) || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) {
        return;
      }

      let marker = markersRef.current.get(parentId);

      if (!marker) {
        marker = new google.maps.Marker({
          map: mapRef.current,
          position,
          title: location.parent_name || 'Parent',
          icon: getMarkerIcon(parentId === hoveredParentId),
          label: {
            text: getMarkerLabel(location.parent_name),
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: '700',
          },
          zIndex: parentId === hoveredParentId ? 2000 : 1000,
        });

        marker.addListener('mouseover', () => {
          setHoveredParentId(parentId);
        });

        marker.addListener('mouseout', () => {
          setHoveredParentId((current) => (current === parentId ? null : current));
        });

        marker.addListener('click', () => {
          setHoveredParentId(parentId);
        });

        markersRef.current.set(parentId, marker);
      } else {
        marker.setPosition(position);
        marker.setTitle(location.parent_name || 'Parent');
        marker.setLabel({
          text: getMarkerLabel(location.parent_name),
          color: '#ffffff',
          fontSize: '11px',
          fontWeight: '700',
        });
      }

      marker.setIcon(getMarkerIcon(parentId === hoveredParentId));
      marker.setZIndex(parentId === hoveredParentId ? 2000 : 1000);
      bounds.extend(position);
      nextMarkerIds.add(parentId);

      if (parentId === hoveredParentId && infoWindowRef.current) {
        highlightedExists = true;
        infoWindowRef.current.setContent(`
          <div style="min-width:180px;">
            <strong>${location.parent_name || 'Parent'}</strong><br />
            ${formatCoordinate(location.latitude)}, ${formatCoordinate(location.longitude)}<br />
            Accuracy: ${location.accuracy ?? '--'} m
          </div>
        `);
        infoWindowRef.current.open({
          anchor: marker,
          map: mapRef.current,
          shouldFocus: false,
        });
      }
    });

    markersRef.current.forEach((marker, parentId) => {
      if (nextMarkerIds.has(parentId)) return;
      marker.setMap(null);
      markersRef.current.delete(parentId);
    });

    if (!locations.length || !highlightedExists) {
      infoWindowRef.current?.close();
    }

    if (locations.length && (shouldAutoFrameRef.current || !hasAutoFramedRef.current)) {
      mapRef.current.fitBounds(bounds, 56);
      hasAutoFramedRef.current = true;
      shouldAutoFrameRef.current = false;
      return;
    }

    if (!locations.length && schoolAddress && (shouldAutoFrameRef.current || !hasAutoFramedRef.current)) {
      geocodeSchoolCenter(GOOGLE_MAPS_API_KEY, schoolAddress).then((center) => {
        mapRef.current?.setCenter(center);
        mapRef.current?.setZoom(15);
      });
      hasAutoFramedRef.current = true;
      shouldAutoFrameRef.current = false;
    }
  }, [hoveredParentId, locations, schoolAddress]);

  const sortedLocations = useMemo(
    () => [...locations].sort((left, right) => String(right.recorded_at || '').localeCompare(String(left.recorded_at || ''))),
    [locations]
  );

  const sortedEvents = useMemo(
    () => [...events].sort((left, right) => String(right.timestamp || '').localeCompare(String(left.timestamp || ''))),
    [events]
  );

  return (
    <div className="pld-page">
      <section className="pld-card">
        <div className="pld-header">
          <div>
            <h2>GPS Diagnostics</h2>
            <p>Recent active parent coordinates from the last 30 minutes.</p>
          </div>
          <button className="pld-refresh" onClick={() => loadLocations({ autoFrame: false })} disabled={loading}>
            Refresh
          </button>
        </div>

        {loadError ? <div className="pld-error">{loadError}</div> : null}
        {mapError ? <div className="pld-error">{mapError}</div> : null}

        <div className="pld-layout">
          <div className="pld-map-wrap">
            <div className="pld-map" ref={mapEl} />
            {!sortedLocations.length && !loading ? (
              <div className="pld-map-empty">No active parent coordinates have been received for this school yet.</div>
            ) : null}
          </div>

          <aside className="pld-sidebar">
            <div className="pld-sidebar-header">
              <h3>Active Parents</h3>
              <span>{sortedLocations.length}</span>
            </div>

            <div className="pld-parent-list">
              {loading ? <div className="pld-empty">Loading coordinates...</div> : null}
              {!loading && !sortedLocations.length ? (
                <div className="pld-empty">Waiting for parent GPS samples.</div>
              ) : null}
              {sortedLocations.map((location) => {
                const parentId = Number(location.contact_id);
                const active = parentId === hoveredParentId;

                return (
                  <button
                    key={location.contact_id}
                    type="button"
                    className={`pld-parent-row ${active ? 'active' : ''}`}
                    onMouseEnter={() => setHoveredParentId(parentId)}
                    onMouseLeave={() => setHoveredParentId((current) => (current === parentId ? null : current))}
                    onClick={() => {
                      setHoveredParentId(parentId);
                      const marker = markersRef.current.get(parentId);
                      if (marker && mapRef.current) {
                        mapRef.current.panTo(marker.getPosition());
                        mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 15, 17));
                      }
                    }}
                  >
                    <div className="pld-parent-row-top">
                      <strong>{location.parent_name}</strong>
                      <span>{formatTime(location.recorded_at)}</span>
                    </div>
                    <div className="pld-parent-row-meta">
                      {formatCoordinate(location.latitude)}, {formatCoordinate(location.longitude)}
                    </div>
                    <div className="pld-parent-row-meta">
                      Accuracy {location.accuracy ?? '--'} m
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      </section>

      <section className="pld-card">
        <div className="pld-header">
          <div>
            <h2>Geofence Event Log</h2>
            <p>Recent enter and exit events recorded for this school.</p>
          </div>
          <span className="pld-event-count">{sortedEvents.length} events</span>
        </div>

        <div className="pld-events">
          {loading && !sortedEvents.length ? (
            <div className="pld-empty">Loading events...</div>
          ) : null}
          {!loading && !sortedEvents.length ? (
            <div className="pld-empty">No geofence enter/exit events have been recorded for this school yet.</div>
          ) : null}

          {sortedEvents.length ? (
            <table className="pld-events-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Parent</th>
                  <th>Event</th>
                  <th>Geofence</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event) => {
                  const action = String(event.action || '').toLowerCase();
                  return (
                    <tr key={event.id}>
                      <td>{formatTime(event.timestamp)}</td>
                      <td>{event.parent_name || `Parent #${event.parent_driver_id}`}</td>
                      <td>
                        <span className={`pld-event-badge ${action === 'enter' ? 'enter' : 'exit'}`}>
                          {action || 'unknown'}
                        </span>
                      </td>
                      <td>{event.carpool_line_name || 'School geofence'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </section>
    </div>
  );
}
